import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import {
    addDays,
    addMonths,
    addWeeks,
    endOfDay,
    endOfMonth,
    endOfWeek,
    format,
    startOfMonth,
    startOfWeek,
} from 'date-fns'
import { es } from 'date-fns/locale'

import {
    AddReservationRequest,
    addReservation as addReservationApi,
    sendHairSalonCancellationEmail,
    sendHairSalonConfirmationEmail,
} from '@/shared/api/reservationsAPI'
import { setDocument } from '@/shared/firebase/DocumentsDAO'
import { useCollection } from '@/shared/firebase/hooks/useCollection'
import { useDocument } from '@/shared/firebase/hooks/useDocument'
import { FSDocument } from '@/shared/firebase/types'
import { EXAMPLE_RESERVATIONS } from '@/shared/mocks/example-reservations'
import { calculateTotalPrice } from '@/shared/utils/pricing'
import { loadPeriodsForDateRange } from '@/shared/utils/pricing-periods'
import { getReservationsByDate } from '@/shared/utils/reservationUtils'
import { findUnscheduledTasks } from '@/widgets/HairSalonCalendar/model/task-utils'
import {
    HairSalonReservation,
    HairSalonReservationStatus,
    HotelReservation,
    PetSize,
    Reservation,
} from '@monorepo/functions/src/types/reservations'

import { useHotelAvailability } from './HotelAvailabilityContext'

const HOTEL_PRICE_AFFECTING_KEYS = [
    'checkInDate',
    'checkOutDate',
    'additionalServices',
    'extras',
    'shopProducts',
] as const

const doesPetChangeAffectPrice = (previousPets: HotelReservation['pets'], nextPets: HotelReservation['pets']) => {
    if (previousPets.length !== nextPets.length) return true

    return nextPets.some((pet, index) => {
        const previous = previousPets[index]
        return !previous || pet.size !== previous.size || pet.weight !== previous.weight
    })
}

const doesHotelUpdateAffectPrice = (
    reservation: HotelReservation,
    updatedData: Partial<Reservation>,
): boolean => {
    if (HOTEL_PRICE_AFFECTING_KEYS.some(key => key in updatedData)) return true

    if (
        'pets' in updatedData &&
        updatedData.pets &&
        doesPetChangeAffectPrice(reservation.pets, updatedData.pets as HotelReservation['pets'])
    ) {
        return true
    }

    if (updatedData.client && updatedData.client.classification !== reservation.client.classification) {
        return true
    }

    return false
}

type ReservationDocument = Reservation & FSDocument

export type { ReservationDocument }

type ExampleReservationsContextType = {
    exampleReservations: ReservationDocument[]
    addExampleReservation: (reservation: ReservationDocument) => void
    updateExampleReservation: (id: string, updatedData: Partial<Reservation>) => void
    deleteExampleReservation: (id: string) => void
    exampleHotelReservations: HotelReservation[]
    exampleHairSalonReservations: HairSalonReservation[]
}

const ExampleReservationsContext = createContext<ExampleReservationsContextType | undefined>(undefined)

export const useExampleReservations = () => {
    const context = useContext(ExampleReservationsContext)
    if (!context) {
        throw new Error('useExampleReservations must be used within an ExampleReservationsProvider')
    }
    return context
}

export const ExampleReservationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    console.log('ExampleReservationsProvider')
    const [exampleReservations, setExampleReservations] = useState<ReservationDocument[]>(() => {
        const storedExampleReservations = localStorage.getItem('exampleReservations')
        return storedExampleReservations
            ? (JSON.parse(storedExampleReservations) as ReservationDocument[])
            : EXAMPLE_RESERVATIONS
    })

    const addExampleReservation = useCallback((reservation: ReservationDocument) => {
        setExampleReservations(prev => {
            const updated = [...prev, reservation]
            localStorage.setItem('exampleReservations', JSON.stringify(updated))
            return updated
        })
    }, [])

    const updateExampleReservation = useCallback((id: string, updatedData: Partial<Reservation>) => {
        setExampleReservations(prev => {
            const updated = prev.map(r =>
                r.id === id
                    ? {
                          ...r,
                          ...updatedData,
                          updatedAt: new Date().toISOString(),
                      }
                    : r,
            )
            localStorage.setItem('exampleReservations', JSON.stringify(updated))
            return updated as ReservationDocument[]
        })
    }, [])

    const deleteExampleReservation = useCallback((id: string) => {
        setExampleReservations(prev => {
            const updated = prev.filter(r => r.id !== id)
            localStorage.setItem('exampleReservations', JSON.stringify(updated))
            return updated
        })
    }, [])

    const exampleHotelReservations = useMemo(() => {
        console.log('ExampleReservationsProvider: exampleReservations', exampleReservations)
        return exampleReservations.filter((r): r is HotelReservation => r.type === 'hotel')
    }, [exampleReservations])

    const exampleHairSalonReservations = useMemo(() => {
        return exampleReservations.filter((r): r is HairSalonReservation => r.type === 'peluqueria')
    }, [exampleReservations])

    return (
        <ExampleReservationsContext.Provider
            value={{
                exampleReservations,
                addExampleReservation,
                updateExampleReservation,
                deleteExampleReservation,
                exampleHotelReservations,
                exampleHairSalonReservations,
            }}
        >
            {children}
        </ExampleReservationsContext.Provider>
    )
}

type ReservationContextType = {
    addReservation: (reservation: Omit<AddReservationRequest, 'id'>) => Promise<ReservationDocument>
    updateReservation: (id: string, updatedData: Partial<Reservation>) => Promise<void>
    deleteReservation: (id: string) => Promise<void>
}

const ReservationContext = createContext<ReservationContextType | undefined>(undefined)

export const useReservations = () => {
    const context = useContext(ReservationContext)
    if (!context) {
        throw new Error('useReservation must be used within a ReservationProvider')
    }
    return context
}

export const useConfirmedDayReservations = (date: string, type: 'hotel' | 'peluqueria') => {
    const { highSeasonPeriods } = useHotelAvailability()
    const [whereClauses] = useMemo(() => {
        if (type === 'peluqueria') {
            return [
                [
                    ['type', '==', type],
                    ['status', '==', 'confirmed'],
                    ['date', '==', date],
                ],
            ]
        } else if (type === 'hotel') {
            return [
                [
                    ['type', '==', type],
                    ['status', '==', 'confirmed'],
                    ['checkInDate', '<=', date],
                    ['checkOutDate', '>=', date],
                ],
            ]
        } else {
            throw new Error('Invalid type')
        }
    }, [date, type])

    const {
        results: reservations,
        isLoading,
        updateDocument,
    } = useCollection<ReservationDocument>({
        path: 'reservations',
        where: whereClauses as any,
    })
    console.log('useConfirmedDayReservations', reservations)

    const { exampleHotelReservations, updateExampleReservation } = useExampleReservations()

    const updateReservation = useCallback(
        async (id: string, updatedData: Partial<Reservation>) => {
            console.log('updateReservation', id, updatedData)
            console.log('reservations', reservations)
            const reservation = reservations.find(r => r.id === id)
            console.log('reservation', reservation)
            const clientClassification = reservation?.client.classification
            let totalPrice = (reservation as HotelReservation | undefined)?.totalPrice ?? 0

            if (
                reservation?.type === 'hotel' &&
                doesHotelUpdateAffectPrice(reservation as HotelReservation, updatedData)
            ) {
                const updatedReservation = { ...reservation, ...updatedData } as HotelReservation
                const periods = await loadPeriodsForDateRange(
                    updatedReservation.checkInDate,
                    updatedReservation.checkOutDate,
                )
                totalPrice = calculateTotalPrice(
                    updatedReservation,
                    periods,
                    clientClassification,
                    highSeasonPeriods,
                ).total
            }

            if (id.startsWith('EXAMPLE_')) {
                updateExampleReservation(id, { ...updatedData, totalPrice })
                return
            }
            if (reservation?.type === 'hotel') {
                updateDocument(id, { ...updatedData, totalPrice })
            } else {
                updateDocument(id, updatedData)
            }
        },
        [reservations, updateDocument, updateExampleReservation, exampleHotelReservations, highSeasonPeriods],
    )
    const allReservations = useMemo(() => {
        return [...reservations, ...getReservationsByDate(exampleHotelReservations, date)]
    }, [reservations, exampleHotelReservations])

    return {
        reservations: allReservations as HotelReservation[],
        isLoading,
        updateReservation,
    }
}
export const useConfirmedHotelDayReservations = (date: string) => useConfirmedDayReservations(date, 'hotel')
export const useConfirmedHotelTodayReservations = () => {
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])
    return useConfirmedHotelDayReservations(today)
}

export const useHairSalonConfirmedDayReservations = (date: string) => useConfirmedDayReservations(date, 'peluqueria')

export const useHairSalonConfirmedTasks = (date: string) => {
    const { reservations: hairSalonreservations } = useHairSalonConfirmedDayReservations(date)
    const { reservations: hotelReservations } = useConfirmedHotelDayReservations(date)
    return useMemo(() => {
        return [...hairSalonreservations, ...hotelReservations].flatMap(r => r.tasks || [])
    }, [hairSalonreservations, hotelReservations])
}

export const useHotelConfirmedTodayReservations = () => {
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])
    const { reservations, isLoading, updateReservation } = useConfirmedHotelDayReservations(today)

    return {
        reservations,
        isLoading,
        updateReservation,
    }
}

export const useTodayCheckIns = () => {
    const { reservations, isLoading } = useHotelConfirmedTodayReservations()
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])

    const checkIns = useMemo(() => {
        return reservations.filter(r => r.checkInDate === today)
    }, [reservations, today])

    return {
        reservations: checkIns,
        isLoading,
    }
}

export const useCheckIns = () => {
    const { updateReservation } = useReservations()
    const { reservations: checkIns, isLoading } = useTodayCheckIns()

    const handleRoomAssign = useCallback(
        (reservationId: string, petName: string, room: string) => {
            const reservation = checkIns.find(r => r.id === reservationId)
            if (reservation) {
                const updatedPets = reservation.pets.map(p => (p.name === petName ? { ...p, roomNumber: room } : p))
                updateReservation(reservationId, { pets: updatedPets })
            }
        },
        [checkIns, updateReservation],
    )

    const handleSizeChange = useCallback(
        async (reservationId: string, petIndex: number, size: PetSize) => {
            const reservation = checkIns.find(r => r.id === reservationId)
            if (!reservation) return

            const updatedPets = [...reservation.pets]
            const oldSize = updatedPets[petIndex].size
            updatedPets[petIndex] = { ...updatedPets[petIndex], size }

            const priceAdjustments: Record<PetSize, number> = {
                pequeño: 20,
                mediano: 25,
                grande: 30,
            }

            const oldPrice = priceAdjustments[oldSize]
            const newPrice = priceAdjustments[size]
            const priceDifference = newPrice - oldPrice

            const newTotalPrice = reservation.totalPrice + priceDifference
            const updateData = {
                pets: updatedPets,
                totalPrice: newTotalPrice,
            }

            await updateReservation(reservationId, updateData)
        },
        [checkIns, updateReservation],
    )

    return {
        checkIns,
        isLoading,
        handleRoomAssign,
        handleSizeChange,
    }
}

export const useTodayCheckOuts = () => {
    const { reservations, isLoading, updateReservation } = useHotelConfirmedTodayReservations()
    console.log('reservations', reservations)
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])

    const checkOuts = useMemo(() => {
        return reservations.filter(r => r.checkOutDate === today)
    }, [reservations, today])

    return {
        reservations: checkOuts,
        isLoading,
        updateReservation,
    }
}
const pendingHotelRequestsWhereClauses = [
    ['type', '==', 'hotel'],
    ['status', 'in', ['pending', 'budget']],
]
export const usePendingHotelRequests = () => {
    const {
        results: reservations,
        isLoading,
        updateDocument: updatePendingHotelRequest,
    } = useCollection<ReservationDocument>({
        path: 'reservations',
        where: pendingHotelRequestsWhereClauses as any,
    })
    console.log('usePendingHotelRequests', reservations)

    const allReservations = useMemo(() => {
        return [...reservations]
    }, [reservations])

    const getCreatedAtMs = (r: HotelReservation): number => r.createdAt?.toMillis() ?? 0

    const pendingReservations = useMemo(() => {
        return allReservations
            .filter((r): r is HotelReservation => r.type === 'hotel' && r.status === 'pending')
            .sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a))
    }, [allReservations])

    const budgets = useMemo(() => {
        return allReservations
            .filter((r): r is HotelReservation => r.type === 'hotel' && r.status === 'budget')
            .sort((a, b) => getCreatedAtMs(b) - getCreatedAtMs(a))
    }, [allReservations])
    console.log('budgets', budgets)

    return {
        pendingReservations,
        budgets,
        isLoading,
        updatePendingHotelRequest,
    }
}

export const usePendingHairSalonExternalReservations = () => {
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])
    const whereClauses = useMemo(
        () => [
            ['type', '==', 'peluqueria'],
            ['status', '==', 'pending'],
            ['date', '>=', today],
        ],
        [today],
    )

    const {
        results: reservations,
        isLoading,
        updateDocument: updatePendingHairSalonRequest,
    } = useCollection<HairSalonReservation>({
        path: 'reservations',
        where: whereClauses as any,
        orderBy: ['date', 'asc'],
    })

    const { exampleReservations } = useExampleReservations()
    const activeExampleReservations = useMemo(() => {
        return exampleReservations
            .filter(r => r.type === 'peluqueria' && r.status === 'pending' && r.date >= today)
            .sort((a, b) => a.date.localeCompare(b.date))
    }, [exampleReservations, today])

    const allReservations = useMemo(() => {
        return [...reservations, ...activeExampleReservations].sort((a, b) => a.date.localeCompare(b.date))
    }, [reservations, activeExampleReservations])

    return {
        pendingReservations: allReservations,
        isLoading,
        updatePendingHairSalonRequest,
    }
}

export const usePendingHairSalonHotelReservations = () => {
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])
    const whereClauses = useMemo(
        () => [
            ['type', '==', 'hotel'],
            ['hairSalonReservationStatus', '==', 'pending'],
            ['status', '==', 'confirmed'],
            ['checkOutDate', '>=', today],
        ],
        [today],
    )

    const {
        results: reservations,
        isLoading,
        updateDocument: updatePendingHairSalonRequest,
    } = useCollection<HotelReservation>({
        path: 'reservations',
        where: whereClauses as any,
        orderBy: ['checkOutDate', 'asc'],
    })

    const { exampleReservations } = useExampleReservations()
    const activeExampleReservations = useMemo(() => {
        return exampleReservations
            .filter(r => r.type === 'hotel' && r.hairSalonReservationStatus === 'pending' && r.checkOutDate >= today)
            .sort((a, b) => a.checkOutDate.localeCompare(b.checkOutDate))
    }, [exampleReservations, today])

    const allReservations = useMemo(() => {
        return [...reservations, ...activeExampleReservations].sort((a, b) =>
            a.checkOutDate.localeCompare(b.checkOutDate),
        )
    }, [reservations, activeExampleReservations])

    return {
        pendingReservations: allReservations,
        isLoading,
        updatePendingHairSalonRequest,
    }
}

export const useHotelDayReservations = (date: string) => {
    console.log('useHotelDayReservations', date)
    const [whereClauses] = useMemo(() => {
        console.log('useHotelDayReservations: useMemo for date', date)
        return [
            [
                ['type', '==', 'hotel'],
                ['status', '==', 'confirmed'],
                ['checkInDate', '<=', date],
                ['checkOutDate', '>=', date],
            ],
        ]
    }, [date])

    const { results: dbReservations, isLoading } = useCollection<ReservationDocument>({
        path: 'reservations',
        where: whereClauses as any,
    })

    const activeExampleReservations = useMemo(() => {
        const storedExampleReservations = localStorage.getItem('exampleReservations')
        const exampleReservations = storedExampleReservations
            ? (JSON.parse(storedExampleReservations) as ReservationDocument[])
            : EXAMPLE_RESERVATIONS

        return exampleReservations.filter(
            r => r.type === 'hotel' && r.status === 'confirmed' && r.checkInDate <= date && r.checkOutDate >= date,
        )
    }, [date])

    const allReservations = useMemo(() => {
        return [...dbReservations, ...activeExampleReservations] as HotelReservation[]
    }, [dbReservations, activeExampleReservations])

    console.log('allReservations', allReservations)
    console.log('dbReservations', dbReservations)
    console.log('activeExampleReservations', activeExampleReservations)

    const stats = useMemo(() => {
        const petsBySize = allReservations.reduce(
            (sizes, reservation) => {
                reservation.pets.forEach(pet => {
                    sizes[pet.size]++
                })
                return sizes
            },
            { grande: 0, mediano: 0, pequeño: 0 },
        )

        return {
            reservations: allReservations,
            totalPets: allReservations.reduce((total, reservation) => total + reservation.pets.length, 0),
            petsBySize,
        }
    }, [allReservations])

    return { ...stats, isLoading }
}

export const useHotelReservationsInPeriod = (startDate: string, endDate: string) => {
    const [whereClauses] = useMemo(() => {
        console.log('useHotelReservationsInPeriod: useMemo')
        return [
            [
                ['type', '==', 'hotel'],
                ['status', '==', 'confirmed'],
                ['checkInDate', '<=', endDate], // TODO: Check if this is correct
                ['checkOutDate', '>=', startDate],
            ],
        ]
    }, [startDate, endDate])

    const { results: dbReservations, isLoading } = useCollection<ReservationDocument>({
        path: 'reservations',
        where: whereClauses as any,
    })

    const activeExampleReservations = useMemo(() => {
        const storedExampleReservations = localStorage.getItem('exampleReservations')
        const exampleReservations = storedExampleReservations
            ? (JSON.parse(storedExampleReservations) as ReservationDocument[])
            : EXAMPLE_RESERVATIONS

        return exampleReservations.filter(
            r =>
                r.type === 'hotel' &&
                r.status === 'confirmed' &&
                r.checkInDate <= endDate &&
                r.checkOutDate >= startDate,
        )
    }, [startDate, endDate])

    const allReservations = useMemo(() => {
        return [...dbReservations, ...activeExampleReservations] as HotelReservation[]
    }, [dbReservations, activeExampleReservations])

    const stats = useMemo(() => {
        const petsBySize = allReservations.reduce(
            (sizes, reservation) => {
                reservation.pets.forEach(pet => {
                    sizes[pet.size]++
                })
                return sizes
            },
            { grande: 0, mediano: 0, pequeño: 0 },
        )

        return {
            reservations: allReservations,
            totalPets: allReservations.reduce((total, reservation) => total + reservation.pets.length, 0),
            petsBySize,
        }
    }, [allReservations])

    return { ...stats, isLoading }
}

export const usePendingHotelReservation = (reservationId: string | null) => {
    const { pendingReservations, budgets, isLoading, updatePendingHotelRequest } = usePendingHotelRequests()
    const pendingReservation = useMemo(
        () => pendingReservations.find(r => r.id === reservationId) || budgets.find(r => r.id === reservationId),
        [pendingReservations, reservationId, budgets],
    )

    return { pendingReservation, isLoading, updatePendingReservation: updatePendingHotelRequest }
}

export const InnerReservationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { addExampleReservation, updateExampleReservation, deleteExampleReservation } = useExampleReservations()
    const isSubmittingRef = React.useRef(false)

    const addReservation = useCallback(
        async (reservation: Omit<AddReservationRequest, 'id'>) => {
            // Prevent concurrent submissions
            if (isSubmittingRef.current) {
                console.log('addReservation: Already submitting, ignoring duplicate call')
                throw new Error('Ya hay una reserva en proceso de creación. Por favor, espera un momento.')
            }

            isSubmittingRef.current = true
            try {
                if (reservation.clientId?.startsWith('EXAMPLE_')) {
                    const newReservation = {
                        ...reservation,
                        id: `EXAMPLE_${Date.now()}`,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    } as ReservationDocument
                    addExampleReservation(newReservation)
                    return newReservation
                }
                const result = await addReservationApi(reservation)
                return result.data as ReservationDocument
            } finally {
                isSubmittingRef.current = false
            }
        },
        [addExampleReservation],
    )

    const updateReservation = useCallback(
        async (id: string, updatedData: Partial<Reservation>) => {
            if (id.startsWith('EXAMPLE_')) {
                updateExampleReservation(id, updatedData)
                return
            }
            await setDocument({
                collectionName: 'reservations',
                id,
                data: {
                    ...updatedData,
                    updatedAt: new Date().toISOString(),
                },
            })
        },
        [updateExampleReservation],
    )

    const deleteReservation = useCallback(async (id: string) => {
        if (id.startsWith('EXAMPLE_')) {
            deleteExampleReservation(id)
            return
        }
        await setDocument({
            collectionName: 'reservations',
            id,
            data: {
                status: 'cancelled',
                updatedAt: new Date().toISOString(),
            },
        })
    }, [])

    return (
        <ReservationContext.Provider
            value={{
                addReservation,
                updateReservation,
                deleteReservation,
            }}
        >
            {children}
        </ReservationContext.Provider>
    )
}

export const calculateHairSalonReservationStatus = (
    reservation: HotelReservation,
    updatedData: Partial<Reservation>,
): HairSalonReservationStatus | undefined => {
    console.log('calculateHairSalonReservationStatus', reservation, updatedData)

    const updatedReservation = { ...reservation, ...updatedData } as HotelReservation
    const updatedHairServices = updatedReservation.additionalServices
        .filter(service => service.type === 'hairdressing')
        .flatMap(service => service.services)

    // If all hairdressing services were removed
    if (updatedHairServices.length === 0) {
        return 'not-requested'
    }

    const unscheduledTasks = findUnscheduledTasks([updatedReservation])
    console.log('unscheduledTasks', unscheduledTasks)
    // If hairdressing services were modified or added
    if (unscheduledTasks.length > 0) {
        return 'pending'
    }

    return 'confirmed'
}

type UseReservationReturn = {
    reservation: ReservationDocument | null | undefined
    updateReservation: (updatedData: Partial<Reservation>) => Promise<void>
    isLoading: boolean
}

export const useReservation = (reservationId: string): UseReservationReturn => {
    const { exampleReservations, updateExampleReservation } = useExampleReservations()
    const { highSeasonPeriods } = useHotelAvailability()
    const {
        document: dbReservation,
        isLoading,
        setDocument,
    } = useDocument<ReservationDocument>({
        collectionName: 'reservations',
        id: reservationId,
    })
    const reservation = useMemo(() => {
        if (isLoading) return null
        return dbReservation || exampleReservations.find(r => r.id === reservationId)
    }, [dbReservation, exampleReservations, reservationId, isLoading])

    const updateReservation = useCallback(
        async (updatedData: Partial<Reservation>) => {
            if (reservation?.type === 'hotel') {
                const updatedReservation = { ...reservation, ...updatedData } as HotelReservation
                const shouldRecalculatePrice = doesHotelUpdateAffectPrice(reservation, updatedData)

                let totalPrice = reservation.totalPrice
                if (shouldRecalculatePrice) {
                    const periods = await loadPeriodsForDateRange(
                        updatedReservation.checkInDate,
                        updatedReservation.checkOutDate,
                    )
                    totalPrice = calculateTotalPrice(
                        updatedReservation,
                        periods,
                        updatedReservation?.client.classification,
                        highSeasonPeriods,
                    ).total
                }

                if (reservationId.startsWith('EXAMPLE_')) {
                    updateExampleReservation(reservationId, {
                        ...updatedData,
                        ...(shouldRecalculatePrice ? { totalPrice } : {}),
                    })
                    return
                }
                const updatedDataHotel = updatedData as HotelReservation

                if (
                    !(reservation.checkoutChangeInitialDate || reservation.checkoutChangeInitialTime) && // Just set it the first time until hair salon acknowledges the change
                    ((updatedDataHotel.checkOutDate && updatedDataHotel.checkOutDate !== reservation.checkOutDate) ||
                        (updatedDataHotel.checkOutTime && updatedDataHotel.checkOutTime !== reservation.checkOutTime))
                ) {
                    updatedDataHotel.checkoutChangeInitialDate = reservation.checkOutDate
                    updatedDataHotel.checkoutChangeInitialTime = reservation.checkOutTime
                }

                const hairSalonReservationStatus = calculateHairSalonReservationStatus(
                    dbReservation as HotelReservation,
                    updatedDataHotel,
                )
                console.log('hairSalonReservationStatus', hairSalonReservationStatus)
                await setDocument({
                    ...updatedDataHotel,
                    ...(shouldRecalculatePrice ? { totalPrice } : {}),
                    hairSalonReservationStatus,
                })
            } else {
                let status = updatedData.status || reservation?.status
                if (!updatedData.status && (updatedData.additionalServices || updatedData.tasks)) {
                    const hasUnscheduledTasks =
                        findUnscheduledTasks([{ ...reservation, ...updatedData } as any]).length > 0
                    status = hasUnscheduledTasks ? 'pending' : 'confirmed'

                    console.log('Saving reservation with status', status)
                    // If all tasks are scheduled, call the confirmation function
                    if (
                        reservation!.type === 'peluqueria' &&
                        reservation!.status === 'pending' &&
                        status === 'confirmed'
                    ) {
                        sendHairSalonConfirmationEmail({ reservationId: reservation!.id })
                    }
                }
                if (updatedData.status === 'cancelled') {
                    sendHairSalonCancellationEmail({ reservationId: reservation!.id })
                }
                await setDocument({
                    ...reservation,
                    ...updatedData,
                    status,
                })
            }
        },
        [setDocument, updateExampleReservation, reservation, reservationId, highSeasonPeriods, dbReservation],
    )

    return { reservation, updateReservation, isLoading }
}

// Billing Hooks
export type BillingPeriod = 'daily' | 'weekly' | 'monthly'

export interface BillingData {
    name: string
    fullDate: Date
    hotel: number
    peluqueria: number
    tienda: number
}

export interface BillingStats {
    totalHotel: number
    totalPeluqueria: number
    totalTienda: number
    totalGeneral: number
}

interface Sale {
    id: string
    productId: string
    productName: string
    price: number
    clientName: string
    reservationId?: string | null
    saleType?: 'withReservation' | 'walkIn'
    createdAt: { seconds: number; nanoseconds: number } | string
    updatedAt: { seconds: number; nanoseconds: number } | string
    userId: string
    userName: string
}

const useBillingSales = (startDate: string, endDate: string) => {
    const { results: sales, isLoading } = useCollection<Sale & FSDocument>({
        path: 'sales',
        orderBy: ['createdAt', 'asc'],
    })

    const filteredSales = useMemo(() => {
        return sales.filter(sale => {
            let saleDate = ''

            if (typeof sale.createdAt === 'string') {
                saleDate = sale.createdAt.split('T')[0]
            } else if (sale.createdAt && typeof sale.createdAt === 'object' && 'seconds' in sale.createdAt) {
                saleDate = format(new Date(sale.createdAt.seconds * 1000), 'yyyy-MM-dd')
            }

            return saleDate >= startDate && saleDate <= endDate
        })
    }, [sales, startDate, endDate])

    return {
        sales: filteredSales,
        isLoading,
    }
}

export const useBillingReservations = (startDate: string, endDate: string) => {
    // Query for hotel reservations that have checkout dates in the period
    const [hotelWhereClauses] = useMemo(() => {
        return [
            [
                ['type', '==', 'hotel'],
                ['status', '==', 'confirmed'],
                ['checkOutDate', '>=', startDate],
                ['checkOutDate', '<=', endDate],
            ],
        ]
    }, [startDate, endDate])
    console.log('startDate', startDate)
    console.log('endDate', endDate)

    // Query for hair salon reservations that have dates in the period
    const [hairSalonWhereClauses] = useMemo(() => {
        return [
            [
                ['type', '==', 'peluqueria'],
                ['status', '==', 'confirmed'],
                ['date', '>=', startDate],
                ['date', '<=', endDate],
            ],
        ]
    }, [startDate, endDate])

    const { results: hotelReservations, isLoading: isLoadingHotel } = useCollection<ReservationDocument>({
        path: 'reservations',
        where: hotelWhereClauses as any,
    })

    const { results: hairSalonReservations, isLoading: isLoadingHairSalon } = useCollection<ReservationDocument>({
        path: 'reservations',
        where: hairSalonWhereClauses as any,
    })

    const { sales, isLoading: isLoadingSales } = useBillingSales(startDate, endDate)

    const allReservations = useMemo(() => {
        return [...hotelReservations, ...hairSalonReservations]
    }, [hotelReservations, hairSalonReservations])
    console.log('allReservations', allReservations)

    return {
        reservations: allReservations,
        sales,
        isLoading: isLoadingHotel || isLoadingHairSalon || isLoadingSales,
    }
}

export const useBillingData = (startDate: Date, endDate: Date, period: BillingPeriod) => {
    const startDateStr = format(startDate, 'yyyy-MM-dd')
    const endDateStr = format(endDate, 'yyyy-MM-dd')

    const { reservations, sales, isLoading } = useBillingReservations(startDateStr, endDateStr)

    const billingData = useMemo(() => {
        const dataMap = new Map<string, BillingData>()
        const reservationIdsInPeriod = new Set(reservations.map(reservation => reservation.id))

        // Initialize periods within the range
        let currentDate = new Date(startDate)
        while (currentDate <= endDate) {
            let key = ''
            let displayName = ''
            let periodStart = new Date(currentDate)

            switch (period) {
                case 'daily':
                    key = format(currentDate, 'yyyy-MM-dd')
                    displayName = format(currentDate, 'dd MMM', { locale: es })
                    break
                case 'weekly':
                    periodStart = startOfWeek(currentDate, { locale: es })
                    key = format(periodStart, 'yyyy-MM-dd')
                    const weekEnd = addDays(periodStart, 6)
                    displayName = `${format(periodStart, 'dd MMM', { locale: es })} - ${format(weekEnd, 'dd MMM', { locale: es })}`
                    break
                case 'monthly':
                    periodStart = startOfMonth(currentDate)
                    key = format(periodStart, 'yyyy-MM-dd')
                    displayName = format(currentDate, 'MMM yyyy', { locale: es })
                    break
            }

            if (!dataMap.has(key)) {
                dataMap.set(key, {
                    name: displayName,
                    fullDate: periodStart,
                    hotel: 0,
                    peluqueria: 0,
                    tienda: 0,
                })
            }

            // Move to next period
            switch (period) {
                case 'daily':
                    currentDate = addDays(currentDate, 1)
                    break
                case 'weekly':
                    currentDate = addWeeks(currentDate, 1)
                    break
                case 'monthly':
                    currentDate = addMonths(currentDate, 1)
                    break
            }
        }

        // Aggregate reservation data
        reservations.forEach(reservation => {
            let dateKey = ''

            if (reservation.type === 'hotel') {
                dateKey = reservation.checkOutDate
            } else if (reservation.type === 'peluqueria') {
                dateKey = reservation.date
            }

            // For weekly and monthly aggregation, we need to map the date to the correct period
            let aggregationKey = dateKey
            if (period === 'weekly' && dateKey) {
                const reservationDate = new Date(dateKey)
                const weekStart = startOfWeek(reservationDate, { locale: es })
                aggregationKey = format(weekStart, 'yyyy-MM-dd')
            } else if (period === 'monthly' && dateKey) {
                const reservationDate = new Date(dateKey)
                const monthStart = startOfMonth(reservationDate)
                aggregationKey = format(monthStart, 'yyyy-MM-dd')
            }

            if (aggregationKey && dataMap.has(aggregationKey)) {
                const data = dataMap.get(aggregationKey)!
                const shopProductsPrice =
                    reservation.shopProducts?.reduce((total, product) => total + product.totalPrice, 0) || 0

                if (reservation.type === 'hotel') {
                    // Keep hotel amount without shop products so they can be accounted in store
                    const reservationPriceWithoutProducts = (reservation.totalPrice || 0) - shopProductsPrice
                    data.hotel += reservationPriceWithoutProducts
                } else if (reservation.type === 'peluqueria') {
                    // Keep hair salon amount without shop products so they can be accounted in store
                    const basePrice =
                        reservation.totalPrice || reservation.finalPrice || reservation.precioEstimado || 0
                    const reservationPriceWithoutProducts = basePrice - shopProductsPrice
                    data.peluqueria += reservationPriceWithoutProducts
                }

                // Store sales linked to reservations are taken from reservation.shopProducts
                data.tienda += shopProductsPrice

                dataMap.set(aggregationKey, data)
            }
        })

        // Aggregate sales data
        sales.forEach(sale => {
            // Sales linked to a reservation are accounted from shopProducts only
            // when the reservation is included in this billing dataset.
            if (sale.reservationId && reservationIdsInPeriod.has(sale.reservationId)) return

            let dateKey = ''

            if (typeof sale.createdAt === 'string') {
                dateKey = sale.createdAt.split('T')[0]
            } else if (sale.createdAt && typeof sale.createdAt === 'object' && 'seconds' in sale.createdAt) {
                dateKey = format(new Date(sale.createdAt.seconds * 1000), 'yyyy-MM-dd')
            }

            // For weekly and monthly aggregation, we need to map the date to the correct period
            let aggregationKey = dateKey
            if (period === 'weekly' && dateKey) {
                const saleDate = new Date(dateKey)
                const weekStart = startOfWeek(saleDate, { locale: es })
                aggregationKey = format(weekStart, 'yyyy-MM-dd')
            } else if (period === 'monthly' && dateKey) {
                const saleDate = new Date(dateKey)
                const monthStart = startOfMonth(saleDate)
                aggregationKey = format(monthStart, 'yyyy-MM-dd')
            }

            if (aggregationKey && dataMap.has(aggregationKey)) {
                const data = dataMap.get(aggregationKey)!
                data.tienda += sale.price || 0
                dataMap.set(aggregationKey, data)
            }
        })

        return Array.from(dataMap.values()).sort((a, b) => a.fullDate.getTime() - b.fullDate.getTime())
    }, [reservations, sales, startDate, endDate, period])

    const stats = useMemo((): BillingStats => {
        const totalTienda = billingData.reduce((sum, item) => sum + item.tienda, 0)
        const totalPeluqueria = billingData.reduce((sum, item) => sum + item.peluqueria, 0)
        const totalHotel = billingData.reduce((sum, item) => sum + item.hotel, 0)

        return {
            totalHotel,
            totalPeluqueria,
            totalTienda,
            totalGeneral: totalHotel + totalPeluqueria + totalTienda,
        }
    }, [billingData])

    return {
        billingData,
        stats,
        isLoading,
    }
}

export const useBillingPeriodData = (selectedDate: Date, period: BillingPeriod) => {
    const { startDate, endDate } = useMemo(() => {
        let start: Date
        let end: Date

        switch (period) {
            case 'daily':
                end = endOfDay(selectedDate)
                start = addDays(end, -11)
                break
            case 'weekly':
                end = endOfWeek(selectedDate, { locale: es })
                start = startOfWeek(addWeeks(end, -11), { locale: es })
                break
            case 'monthly':
                end = endOfMonth(selectedDate)
                start = startOfMonth(addMonths(end, -11))
                break
        }

        return { startDate: start, endDate: end }
    }, [selectedDate, period])

    return useBillingData(startDate, endDate, period)
}

export const ReservationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <ExampleReservationsProvider>
            <InnerReservationProvider>{children}</InnerReservationProvider>
        </ExampleReservationsProvider>
    )
}